import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { authAPI, consentAPI } from '../../../../services/redwolfSdkAPI';
import {
	setAdvertisingTargetingStatus,
	setEmailPreferenceStatus,
	setSMSMessaging
} from '../../../../store/userSlice';
import { useGoogleAnalytics } from '../../../common/hooks/useGoogleAnalytics';
import Modal from '../../../common/components/Modal';
import { getSession } from '../../../../utils/session';
import Spinner from '../../../common/components/Spinner';
import * as Sentry from '@sentry/react';
import { appConfig } from '../../../../utils/appConfig';
import { openNotification } from '../../../../store/commonSlice';
import { notification } from '../../../../utils/constants';
import useRequestRedwolfService from '../../../common/hooks/useRequestRedwolfService';
import { getLocalStorage, getToken } from '../../../../utils/localStorage';
import { isArray } from 'lodash';
import CommunicationSkeleton from '../../../common/skeletons/CommunicationSkeleton';

const EmailPreferences = () => {
	const {
		account: { user },
		advertisingTargetingStatus,
		smsConsentAccepted,
		emailOptin
	} = useSelector((state: any) => state.user);

	const [isEmailPreferencesModalOpen, setIsEmailPreferencesModalOpen] = useState(false);
	const [isEmailOptinChecked, setIsEmailOptinChecked] = useState(false);
	const [isTermsOfServiceChecked, setIsTermsOfServiceChecked] = useState(true);
	const [isSMSConsentChecked, setIsSMSConsentChecked] = useState(true);
	const [isSpinnerLoading, setIsSpinnerLoading] = useState(false);
	const [isSkeletonLoading, setIsSkeletonLoading] = useState(false);
	const [requestRedwolfService] = useRequestRedwolfService();
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();
	const token = getToken();

	const dispatch = useDispatch();
	const userProfile = getLocalStorage('userProfile');

	const transaction = Sentry.startInactiveSpan({ name: 'Email Preference' });

	useEffect(() => {
		setIsEmailOptinChecked(emailOptin ? emailOptin : false);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [emailOptin]);

	useEffect(() => {
		setIsTermsOfServiceChecked(getCurrentTermsOfServiceStatus());

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [advertisingTargetingStatus]);

	useEffect(() => {
		setIsSMSConsentChecked(getSMSStatus());

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [smsConsentAccepted]);

	const getCurrentTermsOfServiceStatus = (): boolean => {
		return advertisingTargetingStatus === 'ACTIVE' ? true : false;
	};

	const getSMSStatus = (): boolean => {
		return smsConsentAccepted === 'ACTIVE' ? true : false;
	};

	const updateCommunicationPreference = (event: any) => {
		event.preventDefault();

		const userConsentDataPayload = {
			consentIds: appConfig.consentIds,
			consentGroupIds: appConfig.consentGroupIds,
			acceptLanguage: appConfig.acceptLanguage,
			consentMessageFormat: appConfig.consentMessageFormat,
			expand: true,
			accessToken: token
		};

		logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_UPDATE_EMAIL_PREFERENCES');

		if (
			emailOptin !== isEmailOptinChecked &&
			(getCurrentTermsOfServiceStatus() !== isTermsOfServiceChecked ||
				getSMSStatus() !== isSMSConsentChecked)
		) {
			setIsSpinnerLoading(true);

			const updateEmailPreference = requestRedwolfService(
				authAPI.updateEmailPreference(token, isEmailOptinChecked)
			);

			const termsOfServicePromise = requestRedwolfService(
				consentAPI.getConsentData(userConsentDataPayload)
			);

			Promise.all([updateEmailPreference, termsOfServicePromise])
				.then(([emailOptinResponse, termsOfServiceResponse]) => {
					if (
						emailOptinResponse.constructor.$metadata$.simpleName === 'Success' &&
						emailOptinResponse.data
					) {
						logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_UPDATE_EMAIL_PREFERENCES_SUCCESS');
						dispatch(setEmailPreferenceStatus(emailOptinResponse.data.emailOptin));
					}

					if (
						termsOfServiceResponse.constructor.$metadata$.simpleName === 'Success' &&
						termsOfServiceResponse.data
					) {
						saveUserConsentRequest(termsOfServiceResponse.data.consentToken);
					}

					transaction && transaction.end();
				})
				.catch((err: any) => {
					Sentry.captureException(err);
					setIsSpinnerLoading(false);
					transaction && transaction.end();
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_UPDATE_EMAIL_PREFERENCES_FAILURE', {
						error: err
					});

					dispatch(
						openNotification({
							type: 'ERROR',
							heading: 'Error',
							body: notification.message.communicationPreferencesError
						})
					);
				});
		} else if (emailOptin !== isEmailOptinChecked) {
			setIsSpinnerLoading(true);

			requestRedwolfService(authAPI.updateEmailPreference(token, isEmailOptinChecked))
				.then((response: any) => {
					setIsSpinnerLoading(false);
					setIsEmailPreferencesModalOpen(false);

					if (response.data) {
						logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_UPDATE_EMAIL_PREFERENCES_SUCCESS');
						dispatch(setEmailPreferenceStatus(response.data.emailOptin));

						dispatch(
							openNotification({
								type: 'SUCCESS',
								heading: 'Success',
								body: notification.message.communicationPreferencesSuccess
							})
						);
					}
					transaction && transaction.end();
				})
				.catch((err: any) => {
					setIsSpinnerLoading(false);
					transaction && transaction.end();
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_UPDATE_EMAIL_PREFERENCES_FAILURE', {
						error: err
					});
					Sentry.captureException(err);

					dispatch(
						openNotification({
							type: 'ERROR',
							heading: 'Error',
							body: notification.message.communicationPreferencesError
						})
					);
				});
		} else if (
			getCurrentTermsOfServiceStatus() !== isTermsOfServiceChecked ||
			getSMSStatus() !== isSMSConsentChecked
		) {
			setIsSpinnerLoading(true);

			requestRedwolfService(consentAPI.getConsentData(userConsentDataPayload))
				.then((response: any) => {
					const consentStatus = response.constructor.$metadata$.simpleName;

					if (consentStatus === 'Success') {
						saveUserConsentRequest(response.data.consentToken);
					} else {
						setIsSpinnerLoading(false);

						Sentry.captureException(new Error('requestConsentMessage:' + response));

						dispatch(
							openNotification({
								type: 'ERROR',
								heading: 'Error',
								body: notification.message.communicationPreferencesError
							})
						);
					}

					transaction && transaction.end();
				})
				.catch((err: any) => {
					setIsSpinnerLoading(false);
					Sentry.captureException(err);
					setIsEmailPreferencesModalOpen(false);

					transaction && transaction.end();
					dispatch(
						openNotification({
							type: 'ERROR',
							heading: 'Error',
							body: notification.message.communicationPreferencesError
						})
					);
				});
		} else {
			setIsEmailPreferencesModalOpen(false);
		}
	};

	const saveUserConsentRequest = (consentToken: string) => {
		const saveUserConsentRequestPayload: any = {
			userId: userProfile.userId,
			userIdType: appConfig.userIdType,
			consentIdsActions: {
				acceptList: [],
				rejectList: []
			},
			consentGroupIdsActions: {
				acceptList: [],
				rejectList: []
			},
			consentToken: getSession('consentToken') || consentToken,
			accessToken: token
		};

		if (getCurrentTermsOfServiceStatus() !== isTermsOfServiceChecked) {
			if (isTermsOfServiceChecked) {
				saveUserConsentRequestPayload.consentIdsActions.acceptList.push('ADVERTISING_TARGETING');
			} else {
				saveUserConsentRequestPayload.consentIdsActions.rejectList.push('ADVERTISING_TARGETING');
			}
		}

		if (getSMSStatus() !== isSMSConsentChecked) {
			if (isSMSConsentChecked) {
				saveUserConsentRequestPayload.consentIdsActions.acceptList.push('SMS_MESSAGING');
			} else {
				saveUserConsentRequestPayload.consentIdsActions.rejectList.push('SMS_MESSAGING');
			}
		}

		requestRedwolfService(consentAPI.saveUserConsent(saveUserConsentRequestPayload))
			.then((response: any) => {
				setIsEmailPreferencesModalOpen(false);

				const consentStatus = response.constructor.$metadata$.simpleName;

				if (consentStatus === 'Success' && response.data) {
					dispatch(setAdvertisingTargetingStatus(isTermsOfServiceChecked ? 'ACTIVE' : 'WITHDRAWN'));
					dispatch(setSMSMessaging(isSMSConsentChecked ? 'ACTIVE' : 'WITHDRAWN'));
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_UPDATE_EMAIL_PREFERENCES_SUCCESS');
					setIsSpinnerLoading(false);
					dispatch(
						openNotification({
							type: 'SUCCESS',
							heading: 'Success',
							body: notification.message.communicationPreferencesSuccess
						})
					);
				} else {
					setIsSpinnerLoading(false);
					Sentry.captureException(new Error('saveUserConsent:' + response));
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_UPDATE_EMAIL_PREFERENCES_FAILURE', {
						error: consentStatus
					});

					dispatch(
						openNotification({
							type: 'ERROR',
							heading: 'Error',
							body: notification.message.communicationPreferencesError
						})
					);
				}

				transaction && transaction.end();
			})
			.catch((err: any) => {
				setIsSpinnerLoading(false);
				Sentry.captureException(err);
				setIsEmailPreferencesModalOpen(false);

				transaction && transaction.end();

				dispatch(
					openNotification({
						type: 'ERROR',
						heading: 'Error',
						body: notification.message.communicationPreferencesError
					})
				);
			});
	};

	const handleModalClose = () => {
		setIsEmailPreferencesModalOpen(false);
		setIsTermsOfServiceChecked(getCurrentTermsOfServiceStatus());
		setIsEmailOptinChecked(emailOptin ? emailOptin : false);
		setIsSMSConsentChecked(getSMSStatus());
	};

	const handleModalOpen = () => {
		setIsEmailPreferencesModalOpen(true);

		if (advertisingTargetingStatus === '' || smsConsentAccepted === '' || emailOptin === null) {
			setIsSkeletonLoading(true);
			const userConsentStatusPayload = {
				userId: userProfile?.userId,
				userIdType: appConfig.userIdType,
				consentIds: appConfig.emailPreferenceConsentIds,
				consentGroupIds: [],
				accessToken: token
			};

			const getEmailPreference = requestRedwolfService(authAPI.getEmailPreference(token));
			const getUserConsentStatus = requestRedwolfService(
				consentAPI.requestUserConsentStatus(userConsentStatusPayload)
			);

			Promise.all([getEmailPreference, getUserConsentStatus])
				.then(([emailOptinResponse, userConsentResponse]) => {
					setIsSkeletonLoading(false);

					if (
						emailOptinResponse.constructor.$metadata$.simpleName === 'Success' &&
						emailOptinResponse.data
					) {
						dispatch(setEmailPreferenceStatus(emailOptinResponse.data.emailOptin));
					}

					if (
						userConsentResponse.constructor.$metadata$.simpleName === 'Success' &&
						userConsentResponse.data
					) {
						if (userConsentResponse.data.userExists) {
							const consents = userConsentResponse.data.consents.toArray();

							if (isArray(consents)) {
								consents.forEach((consent: any) => {
									if (consent.id === 'ADVERTISING_TARGETING') {
										if (
											consent.lastInteractedConsentStatus === consent.currentConsentStatus &&
											consent.lastInteractedVersion === consent.currentConsentVersion
										) {
											dispatch(setAdvertisingTargetingStatus(consent.lastInteractedConsentStatus));
										}
									}

									if (consent.id === 'SMS_MESSAGING') {
										if (
											consent.lastInteractedConsentStatus === consent.currentConsentStatus &&
											consent.lastInteractedVersion === consent.currentConsentVersion
										) {
											dispatch(setSMSMessaging(consent.lastInteractedConsentStatus));
										}
									}
								});
							}
						}
					}
				})
				.catch((err: any) => {
					setIsSkeletonLoading(false);
					Sentry.captureException(err);
				});
		}
	};

	return (
		<div className="contact-info-area email-pre-ctr">
			<div className="contact-info-area-left">
				<h2 className="contact-info" id="edit-email-prefer">
					Communication Preferences{' '}
				</h2>
			</div>

			<div className="contact-info-area-right">
				<button
					className="primary-button acc-info-edit"
					aria-describedby="edit-email-prefer"
					onClick={handleModalOpen}>
					Edit
				</button>

				<Modal
					containerId="emailPreferModal"
					containerClasses="email-pre-modal email-preference"
					show={isEmailPreferencesModalOpen}
					onHide={handleModalClose}
					heading="Communication Preferences ">
					<form className="edit-modal-form">
						{isSkeletonLoading ? (
							<CommunicationSkeleton />
						) : (
							<>
								<div className="form-checkbox CheckBox emailprefer-checkbox">
									<input
										type="checkbox"
										id="terms_of_service"
										autoComplete="off"
										checked={isTermsOfServiceChecked}
										onChange={(event) => setIsTermsOfServiceChecked(event.target.checked)}
									/>
									<label htmlFor="terms_of_service">
										Receive personalized advertisements and tailored emails.
									</label>
								</div>

								<div className="form-checkbox CheckBox emailprefer-checkbox">
									<input
										type="checkbox"
										id="email_optin"
										autoComplete="off"
										checked={isEmailOptinChecked}
										onChange={(event) => setIsEmailOptinChecked(event.target.checked)}
									/>
									<label htmlFor="email_optin">
										Receive emails to keep me updated on all deals and new products.
									</label>
								</div>

								<div className="form-checkbox CheckBox emailprefer-checkbox">
									<input
										type="checkbox"
										id="sms_messaging"
										autoComplete="off"
										checked={isSMSConsentChecked}
										disabled={
											(user && user.phoneNumber) || smsConsentAccepted === 'ACTIVE' ? false : true
										}
										onChange={(event) => setIsSMSConsentChecked(event.target.checked)}
									/>
									<label htmlFor="sms_messaging">
										Receive SMS messaging for VIZIO communications and advertisements
									</label>
								</div>

								<div className="pswdsave-btnwrap">
									<button
										type="submit"
										className="primary-button"
										onClick={updateCommunicationPreference}>
										Save
									</button>
								</div>
							</>
						)}
					</form>
				</Modal>

				{isSpinnerLoading && <Spinner />}
			</div>
		</div>
	);
};

export default EmailPreferences;
